import {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth0 } from "@auth0/auth0-react";
import axios from "../../utils/axiosConfig";

export default function AdminSurveys() {
  const { getAccessTokenSilently } =
    useAuth0();

  const [type, setType] =
    useState("PARTY");

  const [items, setItems] =
    useState([]);

  const [schema, setSchema] =
    useState([]);

  const [search, setSearch] =
    useState("");

  const [expanded, setExpanded] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const token =
          await getAccessTokenSilently({
            authorizationParams: {
              audience:
                "https://someet-api",
            },
          });

        const headers = {
          Authorization:
            `Bearer ${token}`,
        };

        const surveyUrl =
          type === "WORKSHOP"
            ? "/workshop-survey/schema"
            : "/survey/schema";

        const [
          surveyResponse,
          schemaResponse,
        ] = await Promise.all([
          axios.get(
            `/api/admin/surveys?type=${type}`,
            { headers }
          ),

          axios.get(
            surveyUrl,
            { headers }
          ),
        ]);

        console.log(
          "ADMIN SURVEYS:",
          surveyResponse.data
        );

        setItems(
          Array.isArray(
            surveyResponse.data
          )
            ? surveyResponse.data
            : []
        );

        setSchema(
          schemaResponse.data?.sections ??
            schemaResponse.data ??
            []
        );
      } catch (err) {
        console.error(
          "ADMIN SURVEYS ERROR:",
          err.response?.status,
          err.response?.data,
          err
        );

        if (err.response?.status === 403) {
          setError(
            "Nie masz uprawnień do panelu administracyjnego."
          );
        } else if (
          err.response?.status === 401
        ) {
          setError(
            "Sesja wygasła. Zaloguj się ponownie."
          );
        } else {
          setError(
            "Nie udało się pobrać ankiet."
          );
        }

        setItems([]);
        setSchema([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    type,
    getAccessTokenSilently,
  ]);

  const questionTitles =
    useMemo(() => {
      const result = {};

      schema.forEach((section) => {
        (
          section.questions || []
        ).forEach((question) => {
          result[question.id] =
            question.title;
        });
      });

      return result;
    }, [schema]);

  const filtered = useMemo(() => {
    const value =
      search.trim().toLowerCase();

    if (!value) {
      return items;
    }

    return items.filter((item) => {
      return [
        item.firstname,
        item.surname,
        item.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(value);
    });
  }, [items, search]);

  if (loading) {
    return (
      <section>
        <p>Ładowanie ankiet...</p>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <div className="admin-error">
          {error}
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="admin-filters">
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setExpanded(null);
          }}
        >
          <option value="PARTY">
            Ankieta spotkań
          </option>

          <option value="WORKSHOP">
            Ankieta warsztatowa
          </option>
        </select>

        <input
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
          placeholder="Szukaj osoby..."
        />
      </div>

      <p className="admin-count">
        Ankiet: {filtered.length}
      </p>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Osoba</th>
              <th>Email</th>
              <th>Typ</th>
              <th>Status</th>
              <th>Wysłano</th>
              <th />
            </tr>
          </thead>

          <tbody>
            {filtered.map((item) => (
              <Fragment
                key={`${type}-${item.userId}`}
              >
                <tr>
                  <td>
                    {item.firstname || "-"}{" "}
                    {item.surname || ""}
                  </td>

                  <td>
                    {item.email || "-"}
                  </td>

                  <td>
                    {item.surveyType}
                  </td>

                  <td>
                    {item.submitted
                      ? item.version
                        ? `Wysłana · v${item.version}`
                        : "Wysłana"
                      : "W trakcie"}
                  </td>

                  <td>
                    {item.submittedAt
                      ? new Date(
                          item.submittedAt
                        ).toLocaleString(
                          "pl-PL"
                        )
                      : item.lastUpdatedAt
                        ? `Ostatnia zmiana: ${new Date(
                            item.lastUpdatedAt
                          ).toLocaleString(
                            "pl-PL"
                          )}`
                        : "-"}
                  </td>

                  <td>
                    <button
                      className="admin-small-button"
                      onClick={() =>
                        setExpanded(
                          expanded ===
                            item.userId
                            ? null
                            : item.userId
                        )
                      }
                    >
                      {expanded ===
                      item.userId
                        ? "Ukryj"
                        : "Odpowiedzi"}
                    </button>
                  </td>
                </tr>

                {expanded ===
                  item.userId && (
                  <tr className="admin-expanded">
                    <td colSpan="6">
                      <div className="admin-survey-answers">
                        {Object.entries(
                          item.answers || {}
                        ).length === 0 ? (
                          <div className="admin-answer">
                            Brak zapisanych odpowiedzi.
                          </div>
                        ) : (
                          Object.entries(
                            item.answers || {}
                          ).map(
                            ([
                              questionId,
                              answer,
                            ]) => (
                              <div
                                className="admin-answer"
                                key={questionId}
                              >
                                <strong>
                                  {questionTitles[
                                    questionId
                                  ] ||
                                    questionId}
                                </strong>

                                <AnswerValue
                                  value={
                                    answer
                                  }
                                />
                              </div>
                            )
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}

            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan="6"
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                  }}
                >
                  Brak ankiet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AnswerValue({ value }) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return <p>Brak odpowiedzi</p>;
  }

  if (Array.isArray(value)) {
    return (
      <p>
        {value.length
          ? value.join(", ")
          : "Brak odpowiedzi"}
      </p>
    );
  }

  if (
    typeof value === "object"
  ) {
    return (
      <div className="admin-object-answer">
        {Object.entries(value).map(
          ([key, values]) => (
            <div key={key}>
              <b>{key}:</b>{" "}
              {Array.isArray(values)
                ? values.join(", ")
                : String(values)}
            </div>
          )
        )}
      </div>
    );
  }

  return <p>{String(value)}</p>;
}